import React, { useState, useEffect, useRef } from 'react';
import { EventData } from '../../types/Event';
import { EventDetailsForm} from '../shared/EventDetailsForm';
import { Box, Button, LoadingOverlay } from '@mantine/core';
import { updateEvent } from '../../api/Event';

interface EventDetailsModalProps {
    eventData: EventData;
    onEventUpdate: (event: Partial<EventData>) => void;
}

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
    eventData,
    onEventUpdate
}) => {
    // Function to clean up localStorage when modal is closed
    const cleanupStorage = () => {
        try {
            localStorage.removeItem(`event-edit-${eventData.key}`);
            console.log('[EventDetailsModal] Cleaned up localStorage for event:', eventData.key);
        } catch (e) {
            console.error('[EventDetailsModal] Error cleaning up localStorage:', e);
        }
    };
    // State to track the current event data with local changes
    const [currentEvent, setCurrentEvent] = useState<EventData>(() => {
        // Try to load saved event data from localStorage
        const storageKey = `event-edit-${eventData.key}`;
        const savedEventData = localStorage.getItem(storageKey);
        
        if (savedEventData) {
            try {
                const parsedData = JSON.parse(savedEventData);
                // Verify this is for the current event
                if (parsedData && parsedData.key === eventData.key) {
                    console.log('[EventDetailsModal] Loaded saved event data from localStorage');
                    return parsedData;
                }
            } catch (e) {
                console.error('[EventDetailsModal] Failed to parse saved event data:', e);
            }
        }
        return eventData;
    });
    
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const storageKeyRef = useRef<string>(`event-edit-${eventData.key}`);

    // Update local state when the prop changes (different event being edited)
    useEffect(() => {
        // If the event key changes, update our storage key
        if (eventData.key !== storageKeyRef.current.split('-').slice(-1)[0]) {
            // Clear old storage data
            localStorage.removeItem(storageKeyRef.current);
            // Update storage key reference
            storageKeyRef.current = `event-edit-${eventData.key}`;
            // Set the new event data
            setCurrentEvent(eventData);
            setHasChanges(false);
        }
    }, [eventData.key]);

    // Handle local updates from the form
    const handleEventUpdate = (partialUpdate: Partial<EventData>) => {
        // First, ensure we have the most up-to-date event data
        let baseEventData = currentEvent;
        
        // Try to load the latest data from localStorage
        try {
            const savedEventData = localStorage.getItem(storageKeyRef.current);
            if (savedEventData) {
                const parsedData = JSON.parse(savedEventData);
                if (parsedData && parsedData.key === eventData.key) {
                    baseEventData = parsedData;
                }
            }
        } catch (e) {
            console.error('[EventDetailsModal] Failed to load from localStorage:', e);
        }
        
        // Create a complete updated event by merging the current event with the partial update
        const updatedEvent: EventData = {
            ...baseEventData,
            ...partialUpdate
        };
        
        // Save to localStorage before updating state
        localStorage.setItem(storageKeyRef.current, JSON.stringify(updatedEvent));
        console.log('[EventDetailsModal] Saved event data to localStorage');
        
        // Update local state only, don't propagate to parent yet
        setCurrentEvent(updatedEvent);
        setHasChanges(true);
    };

    // Handle save button click
    const handleSave = async () => {
        setIsSaving(true);
        try {
            // Get the latest data from localStorage
            let finalEventData = currentEvent;
            try {
                const savedEventData = localStorage.getItem(storageKeyRef.current);
                if (savedEventData) {
                    const parsedData = JSON.parse(savedEventData);
                    if (parsedData && parsedData.key === eventData.key) {
                        finalEventData = parsedData;
                    }
                }
            } catch (e) {
                console.error('[EventDetailsModal] Failed to load from localStorage:', e);
            }
            
            // Call the API to update the event
            await updateEvent(finalEventData);
            console.log('[EventDetailsModal] Event updated successfully:', finalEventData);
            
            // Clear localStorage for this event
            cleanupStorage();
            
            // Propagate the update to the parent component
            onEventUpdate(finalEventData);
        } catch (error) {
            console.error('[EventDetailsModal] Failed to update event:', error);
            // You could add error handling UI here
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box style={{ position: 'relative' }}>
            {/* Event details form */}
            <EventDetailsForm
                eventData={currentEvent}
                onEventUpdate={handleEventUpdate}
            />
            
            {/* Save button */}
            <Button
                variant="filled"
                mt="xl"
                fullWidth
                onClick={handleSave}
                disabled={isSaving}
            >
                Save Changes
            </Button>
        </Box>
    );
};